<template>
    <section class="panel">
        <ChatConversationHeader
            :title="title"
            :subtitle="subtitle"
            :show-menu="showMenu"
            :is-admin="isAdmin"
            :is-authenticated="isAuthenticated"
            @toggle-sidebar="$emit('toggle-sidebar')"
            @share="$emit('share')"
            @export="$emit('export')"
            @publish="$emit('publish')"
            @rename="$emit('rename', $event)"
            @delete-thread="$emit('delete-thread')"
        />
        <ChatContextBar v-if="contextChips.length" :chips="contextChips" />

        <ChatMessages
            :messages="messages"
            :is-loading-history="historyLoading"
            :user-initials="userInitials"
            :user-name="userName"
            :selection-mode="selection.active"
            :selected-ids="selection.ids"
            :is-streaming="isStreaming"
            :active-message-id="activeMessageId"
            @regenerate="$emit('regenerate', $event)"
            @copy="$emit('copy', $event)"
            @feedback="(message, value) => $emit('feedback', message, value)"
            @followup="(message, action) => $emit('followup', message, action)"
            @suggest="(text, template) => $emit('suggest', text, template)"
            @toggle-select="$emit('toggle-select', $event)"
            @update-card-view="$emit('update-card-view', $event)"
            @edit="$emit('edit', $event)"
            @version-prev="$emit('version-prev', $event)"
            @version-next="$emit('version-next', $event)"
        />

        <transition name="fade-up">
            <div v-if="selection.active" class="selection-bar">
                <div class="selection-info">
                    <strong>{{ selection.ids.length }}</strong>
                    <span>{{ messageWord }}</span>
                    <button type="button" class="link" @click="$emit('select-all')">
                        Выбрать все
                    </button>
                </div>
                <div class="selection-actions">
                    <button type="button" class="ghost-btn" @click="$emit('cancel-selection')">
                        Отмена
                    </button>
                    <button
                        type="button"
                        class="primary-btn"
                        :disabled="selection.ids.length === 0"
                        @click="$emit('confirm-selection')"
                    >
                        {{ confirmLabel }}
                    </button>
                </div>
            </div>
        </transition>

        <ChatAccessBanner
            v-if="!selection.active"
            :access-tier="accessTier"
            :remaining="remaining"
            :total="total"
            :is-limit-reached="isLimitReached"
            :reason="accessReason"
            @upgrade="$emit('upgrade')"
        />

        <ChatInputArea
            v-if="!selection.active"
            ref="inputRef"
            :chips="inputChips"
            :is-streaming="isStreaming"
            :is-limit-reached="isLimitReached"
            :has-messages="messages.length > 0"
            :filter-selection="filterSelection"
            @send="$emit('send', $event)"
            @stop="$emit('stop')"
            @remove-chip="$emit('remove-chip', $event)"
            @update-filters="$emit('update-filters', $event)"
        />
    </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import ChatConversationHeader from './ChatConversationHeader.vue'
import ChatContextBar from './ChatContextBar.vue'
import ChatMessages from './ChatMessages.vue'
import ChatAccessBanner from '@/components/chat/input/ChatAccessBanner.vue'
import ChatInputArea from '@/components/chat/input/ChatInputArea.vue'

const props = defineProps({
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    contextChips: { type: Array, default: () => [] },
    inputChips: { type: Array, default: () => [] },
    messages: { type: Array, default: () => [] },
    isLoadingHistory: { type: Boolean, default: false },
    userInitials: { type: String, default: 'ГМ' },
    userName: { type: String, default: '' },
    isStreaming: { type: Boolean, default: false },
    activeMessageId: { type: String, default: null },
    isLimitReached: { type: Boolean, default: false },
    accessTier: { type: String, default: 'guest' },
    accessReason: { type: String, default: null },
    remaining: { type: Number, default: 0 },
    total: { type: Number, default: 0 },
    showMenu: { type: Boolean, default: false },
    isAdmin: { type: Boolean, default: false },
    // Прокидываем в шапку — она скроет кнопку «Экспорт» гостям.
    isAuthenticated: { type: Boolean, default: false },
    filterSelection: {
        type: Object,
        default: () => ({}),
    },
    selection: {
        type: Object,
        default: () => ({ active: false, kind: null, ids: [] }),
    },
})

const historyLoading = computed(() => {
    const v = props.isLoadingHistory
    if (typeof v === 'boolean') return v
    if (v && typeof v === 'object' && 'value' in v) return Boolean(v.value)
    return Boolean(v)
})

defineEmits([
    'send',
    'stop',
    'remove-chip',
    'update-filters',
    'regenerate',
    'edit',
    'version-prev',
    'version-next',
    'copy',
    'feedback',
    'followup',
    'suggest',
    'share',
    'export',
    'publish',
    'rename',
    'delete-thread',
    'toggle-sidebar',
    'upgrade',
    'toggle-select',
    'select-all',
    'cancel-selection',
    'confirm-selection',
])

function plural(n, forms) {
    const abs = Math.abs(n) % 100
    if (abs >= 11 && abs <= 14) return forms[2]
    const last = abs % 10
    if (last === 1) return forms[0]
    if (last >= 2 && last <= 4) return forms[1]
    return forms[2]
}

const messageWord = computed(() => {
    return plural(props.selection.ids.length, ['сообщение', 'сообщения', 'сообщений']) + ' выбрано'
})

const confirmLabel = computed(() => {
    const n = props.selection.ids.length
    if (n === 0) return 'Выберите сообщения'
    if (props.selection.kind === 'publish') return `Опубликовать (${n})`
    if (props.selection.kind === 'my-report') return `Сохранить (${n})`
    if (props.selection.kind === 'export') return `Экспорт PDF (${n})`
    return `Создать ссылку (${n})`
})

const inputRef = ref(null)

defineExpose({
    focusInput() {
        inputRef.value?.focus()
    },
    setInputText(value) {
        inputRef.value?.setText(value)
    },
})
</script>

<style scoped>
.panel {
    flex: 1;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background: white;
    overflow: hidden;
}

.selection-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 24px;
    background: #f7f7f7;
    border-top: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
}

.selection-info {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #23282C;
    flex-wrap: wrap;
}

.selection-info strong {
    font-size: 14px;
    font-weight: 700;
    color: #E30613;
}

.selection-info .link {
    background: transparent;
    border: 0;
    color: #5f5f5f;
    font-size: 12px;
    cursor: pointer;
    text-decoration: underline;
    padding: 2px 4px;
}

.selection-info .link:hover { color: #23282C; }

.selection-actions {
    display: flex;
    gap: 8px;
}

.ghost-btn {
    background: transparent;
    border: 1px solid #e0e0e0;
    padding: 7px 12px;
    border-radius: 6px;
    font-family: inherit;
    font-size: 12.5px;
    color: #23282C;
    cursor: pointer;
}

.ghost-btn:hover { background: #f0f0f0; }

.primary-btn {
    background: #E30613;
    color: white;
    border: 0;
    padding: 8px 14px;
    border-radius: 6px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.primary-btn:hover { background: #C0050F; }
.primary-btn:disabled { opacity: 0.55; cursor: not-allowed; }

.fade-up-enter-active,
.fade-up-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
}

.fade-up-enter-from,
.fade-up-leave-to {
    opacity: 0;
    transform: translateY(6px);
}

@media (max-width: 720px) {
    .selection-bar {
        padding: 10px 12px;
        flex-direction: column;
        align-items: stretch;
    }
    .selection-actions { justify-content: flex-end; }
}
</style>
